import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  AppFrame,
  Avatar,
  Breadcrumbs,
  BuildingIcon,
  Button,
  ErrorState,
  GlobeIcon,
  KeyRoundIcon,
  LanguagesIcon,
  LayersIcon,
  LayoutGridIcon,
  LogOutIcon,
  MapIcon,
  MenuButton,
  menuClasses,
  MoonIcon,
  NetworkIcon,
  ServerIcon,
  ScrollTextIcon,
  SearchIcon,
  ShieldCheckIcon,
  ShieldIcon,
  Sidebar,
  SunIcon,
  Topbar,
  TopbarChip,
  TopbarMenuChip,
  TopbarToggleChip,
  shortenId,
  UserCogIcon,
  UserIcon,
  UsersIcon,
  BanIcon,
  DownloadIcon,
  ExternalLinkIcon,
  isThemeName,
  type Crumb,
  type SidebarGroup,
} from "@strust/ui";
import { css } from "@strust/ui/css";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useIntl, type MessageDescriptor } from "react-intl";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import { ApiRequestError } from "../../../shared/api/client.ts";
import { usePermissions } from "../../../shared/use-permissions.ts";
import { useLogout } from "../../access/api/session.ts";
import { navMessages, userMenuMessages } from "../../access/messages.ts";
import { useOperator } from "../../platform/api/operators.ts";
import { useOperatorOrganization } from "../../platform/api/organizations.ts";
import { useLocale } from "../../../shared/i18n/i18n-provider.tsx";
import {
  CurrentInstallationBadge,
  useInstallationEnvironment,
} from "../../../shared/installation-gate.tsx";
import { useInstallationAdministration } from "../../../shared/installation.ts";
import { integrationMessages } from "../../integration/messages.ts";
import { LOCALES, isLocale } from "../../../shared/i18n/locale.ts";
import { appMessages } from "../../../shared/i18n/messages.ts";
import { EXPLORER_VERSION } from "../../../shared/version.ts";
import { describeError } from "../../access/model/error-message.ts";
import { useDownloadLlmsFull, useOpenDocs } from "../api/docs-access.ts";
import { useMe, useUpdateSettings } from "../api/me.ts";
import { useTheme } from "../../../shared/theme/theme-provider.tsx";
import {
  languageChipMessage,
  navFooterMessage,
  shellMessages,
  themeChipMessage,
  timeZoneMessage,
  userRoleMessage,
} from "../messages.ts";
import { isReleaseFlagOn } from "../../../shared/release-flags.ts";
import { breadcrumbSpecs, platformOperatorIdOf } from "../model/breadcrumbs.ts";
import { NotificationsMenu } from "../components/notifications-menu.tsx";
import { initialsOf } from "../model/initials.ts";
import { formatUtcOffset } from "../model/utc-offset.ts";

const NAV_COLLAPSED_KEY = "strust.explorer.sidenav.collapsed";

const ROLE_MESSAGES = {
  viewer: shellMessages.roleViewer,
  analyst: shellMessages.roleAnalyst,
  administrator: shellMessages.roleAdministrator,
} as const;
const ROLE_RANK = ["viewer", "analyst", "administrator"] as const;

const LOCALE_LABELS = { en: appMessages.languageEn, "pt-BR": appMessages.languagePtBR } as const;

/**
 * The investigation links of the side menu (paths are the Explorer routes): only users holding
 * `explorer.read` on an operator see them.
 */
const NAV_ITEMS = [
  { id: "/", message: shellMessages.navSearch, icon: <SearchIcon /> },
  { id: "/users", message: shellMessages.navUsers, icon: <UsersIcon /> },
  { id: "/blocklist", message: shellMessages.navBlocklist, icon: <BanIcon /> },
  { id: "/map", message: shellMessages.navMap, icon: <MapIcon /> },
] as const;

/** Administration links: only users holding `users.manage` on an operator see them. */
const ADMIN_NAV_ITEMS = [
  { id: "/console-users", message: navMessages.consoleUsers, icon: <UserCogIcon /> },
  { id: "/identity-provider", message: navMessages.identityProvider, icon: <KeyRoundIcon /> },
] as const;

/**
 * The customer's "Integration" screen: only in a customer installation (`GET /installation`
 * answers `platform_administration: disabled`) and only to users holding `configuration.manage`
 * or `credentials.manage` on an operator (adopt-installation-per-customer design D14 item 4).
 */
const INTEGRATION_NAV_ITEM = {
  id: "/integration",
  message: integrationMessages.nav,
  icon: <LayersIcon />,
} as const;

/** The audit log link: only users holding `audit.read` on an operator see it. */
const AUDIT_NAV_ITEM = {
  id: "/audit",
  message: navMessages.audit,
  icon: <ScrollTextIcon />,
} as const;

/** Platform area links: only Platform Administrators (a platform permission) see them. */
const PLATFORM_NAV_ITEMS = [
  {
    id: "/platform/organizations",
    message: shellMessages.navPlatformOrganizations,
    icon: <NetworkIcon />,
  },
  {
    id: "/platform/operators",
    message: shellMessages.navPlatformOperators,
    icon: <BuildingIcon />,
  },
  {
    id: "/platform/administrators",
    message: shellMessages.navPlatformAdministrators,
    icon: <ShieldIcon />,
  },
] as const;

/** The Fleet tab: only with `platform.fleet.manage`, and only where the build has fleet.console. */
const FLEET_NAV_ITEM = {
  id: "/platform/fleet",
  message: shellMessages.navPlatformFleet,
  icon: <ServerIcon />,
} as const;

/** Screens that use the `workspace` layout instead of the padded page. */
function isWorkspaceRoute(pathname: string): boolean {
  return pathname === "/map" || pathname.startsWith("/map/");
}

function readCollapsed(): boolean {
  return window.localStorage.getItem(NAV_COLLAPSED_KEY) === "true";
}

// The frame is a `100dvh` grid: the side menu and the top bar never scroll away and only the main
// region scrolls (design D1 of `align-explorer-screens-with-design`).
const CONTENT = css({
  minHeight: "full",
  p: { base: "4", md: "6", xl: "8" },
  "@media print": { p: "0" },
});
// The `workspace` layout (design D3, overview map): no content padding, the screen fills the main
// region and lays out its own scrolling parts.
const WORKSPACE = css({
  display: "flex",
  flexDirection: "column",
  height: "full",
  minHeight: "0",
  "& > main": { flex: "1", minHeight: "0" },
});
const SKIP_LINK = css({
  position: "absolute",
  insetInlineStart: "2",
  top: "-12",
  zIndex: "skipLink",
  px: "3",
  py: "2",
  bg: "accent",
  color: "on.accent",
  borderRadius: "sm",
  fontWeight: "semibold",
  _focus: { top: "2" },
});
const NOTICE = css({ m: "0", px: "6", py: "2", bg: "warn.soft", color: "warn.fg", fontSize: "md" });
const MONO = css({ fontFamily: "mono" });
const APPS_ERROR = css({ color: "deny.fg", fontSize: "sm" });
const MENU_TEXT = css({ m: "0", px: "2.5", py: "1.5", fontSize: "md", color: "text.2" });
const MENU_NAME = css({
  m: "0",
  px: "2.5",
  pt: "1.5",
  fontSize: "md",
  fontWeight: "semibold",
  color: "text",
  overflowWrap: "anywhere",
});

/**
 * The authenticated frame of every screen (Steel Zero Trust): side menu with the Investigate,
 * Administer and Platform groups, version and the installation's environment; top bar with
 * breadcrumbs, the fixed installation badge (no selector, design D7 of
 * bind-operator-environment-to-installation), displayed time zone, language, theme, notifications,
 * apps and user menu. Settings come from `GET /me`; language and theme changes are saved with
 * `PUT /me/settings`.
 */
export function AppShell({ children }: { readonly children: ReactNode }) {
  const intl = useIntl();
  const { locale, setLocale, timeZone, setTimeZone } = useLocale();
  const { theme, setTheme } = useTheme();
  const environment = useInstallationEnvironment();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const me = useMe();
  const saveSettings = useUpdateSettings();
  const navigate = useNavigate();
  const logout = useLogout();
  const openDocs = useOpenDocs();
  const downloadLlms = useDownloadLlmsFull();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canManageUsers = permissions.can("users.manage");
  const canReadAudit = permissions.can("audit.read");
  const canInvestigate = permissions.can("explorer.read");
  // A customer installation has no Platform area (ADR 0022): its menu is hidden whatever /me says.
  const customerInstallation = useInstallationAdministration(me.data !== undefined).customer;
  const hasPlatformAccess = permissions.hasPlatformAccess && !customerInstallation;
  const canIntegrate =
    customerInstallation &&
    (permissions.can("configuration.manage") || permissions.can("credentials.manage"));
  const canManageFleet =
    isReleaseFlagOn("fleet.console") && permissions.canPlatform("platform.fleet.manage");
  const [collapsed, setCollapsed] = useState(readCollapsed);

  // The stored settings of the user win over the browser's remembered choice, once, unless the user
  // already picked a language or theme in this session (their explicit choice is never overridden).
  const settings = me.data;
  const languageChosen = useRef(false);
  const themeChosen = useRef(false);
  const settingsApplied = useRef(false);
  useEffect(() => {
    if (!settings) return;
    setTimeZone(settings.time_zone);
    if (settingsApplied.current) return;
    settingsApplied.current = true;
    if (!languageChosen.current && isLocale(settings.locale)) setLocale(settings.locale);
    if (!themeChosen.current && isThemeName(settings.theme)) setTheme(settings.theme);
  }, [settings, setLocale, setTimeZone, setTheme]);

  // Coming back to a tab that sat idle: check the session now, so an ended one sends the user to
  // login instead of failing on their first click (30 min idle / 12 h absolute, enforced by iam).
  const recheckSession = me.refetch;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void recheckSession();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [recheckSession]);

  const groups = useMemo<readonly SidebarGroup[]>(() => {
    const toItems = (
      list: readonly { id: string; message: MessageDescriptor; icon: ReactNode }[],
    ) =>
      list.map((item) => ({
        id: item.id,
        label: intl.formatMessage(item.message),
        icon: item.icon,
        current: item.id === "/" ? pathname === "/" : pathname.startsWith(item.id),
      }));
    return [
      {
        id: "investigate",
        label: intl.formatMessage(shellMessages.navGroupInvestigate),
        items: toItems(canInvestigate ? NAV_ITEMS : []),
      },
      {
        id: "administer",
        label: intl.formatMessage(shellMessages.navGroupAdminister),
        items: toItems([
          ...(canManageUsers ? ADMIN_NAV_ITEMS : []),
          ...(canIntegrate ? [INTEGRATION_NAV_ITEM] : []),
          ...(canReadAudit ? [AUDIT_NAV_ITEM] : []),
        ]),
      },
      {
        id: "platform",
        label: intl.formatMessage(shellMessages.navGroupPlatform),
        items: toItems([
          ...(hasPlatformAccess ? PLATFORM_NAV_ITEMS : []),
          ...(canManageFleet && !customerInstallation ? [FLEET_NAV_ITEM] : []),
        ]),
      },
    ];
  }, [
    intl,
    pathname,
    canInvestigate,
    canManageUsers,
    canIntegrate,
    canReadAudit,
    hasPlatformAccess,
    canManageFleet,
    customerInstallation,
  ]);

  const offset = formatUtcOffset(timeZone, new Date(), locale);
  const role = settings?.operators
    .map((operator) => operator.role)
    .sort((a, b) => ROLE_RANK.indexOf(b) - ROLE_RANK.indexOf(a))[0];
  // spec console-account-settings, "Platform Administrator menu" (design D11): a Platform
  // Administrator has no operator role, so the menu names the platform role (`/me.platform_role`)
  // instead of the "No operator access" fallback.
  const isPlatformAdministrator = settings?.platform_role === "platform_administrator";
  const roleLabel = role
    ? intl.formatMessage(userRoleMessage, { role: intl.formatMessage(ROLE_MESSAGES[role]) })
    : isPlatformAdministrator
      ? intl.formatMessage(userRoleMessage, {
          role: intl.formatMessage(shellMessages.rolePlatformAdministrator),
        })
      : intl.formatMessage(shellMessages.userNoRole);

  const displayName = settings?.display_name;

  const toggleNav = () => {
    setCollapsed((value) => {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, String(!value));
      return !value;
    });
  };

  const search: Record<string, unknown> = useRouterState({
    select: (state) => state.location.search as Record<string, unknown>,
  });
  const operatorId = platformOperatorIdOf(pathname);
  const operator = useOperator(operatorId ?? "", hasPlatformAccess && operatorId !== undefined);
  // The trail goes through the operator's organization (design D9 of
  // reorganize-platform-area-by-organization); the page reads the same cached query.
  const operatorOrganization = useOperatorOrganization(
    operatorId ?? "",
    hasPlatformAccess && operatorId !== undefined,
  ).data?.organization;
  const crumbs: readonly Crumb[] = breadcrumbSpecs(
    pathname,
    typeof search.tab === "string" ? search.tab : undefined,
    typeof search.q === "string" && search.q.trim() !== "" ? search.q.trim() : undefined,
    {
      organization: operatorOrganization
        ? { id: operatorOrganization.organization_id, name: operatorOrganization.name }
        : undefined,
    },
  ).map((spec, index, all) => {
    // The operator crumb shows its name; its id (shortened, mono) until the operator is read.
    const isOperator = spec.id === "operator";
    const raw = isOperator ? (operator.data?.name ?? spec.text ?? "") : (spec.text ?? "");
    const text = spec.message
      ? intl.formatMessage(spec.message)
      : spec.shorten
        ? shortenId(raw)
        : isOperator && !operator.data
          ? shortenId(raw)
          : raw;
    const label = (
      <span
        className={spec.mono || (isOperator && !operator.data) ? MONO : undefined}
        title={spec.text && text !== raw ? raw : undefined}
      >
        {text}
      </span>
    );
    const isLast = index === all.length - 1;
    return {
      id: spec.id,
      node:
        spec.to && !isLast ? (
          <Link to={spec.to} search={spec.linkSearch ?? {}}>
            {label}
          </Link>
        ) : (
          label
        ),
    };
  });
  const themeText = intl.formatMessage(
    theme === "light" ? shellMessages.themeLight : shellMessages.themeDark,
  );

  return (
    <>
      <a className={SKIP_LINK} href="#main-content">
        {intl.formatMessage(shellMessages.skipToContent)}
      </a>
      <AppFrame
        sidebar={
          <Sidebar
            label={intl.formatMessage(shellMessages.navLabel)}
            groups={groups}
            brandName={intl.formatMessage(shellMessages.logo)}
            productName={intl.formatMessage(shellMessages.productName)}
            renderBrand={({ className, children: content }) => (
              <Link
                to="/"
                className={className}
                aria-label={intl.formatMessage(shellMessages.brandHome)}
              >
                {content}
              </Link>
            )}
            collapsed={collapsed}
            onToggle={toggleNav}
            collapseLabel={intl.formatMessage(shellMessages.navCollapse)}
            expandLabel={intl.formatMessage(shellMessages.navExpand)}
            renderLink={({ item, className, children: content }) => (
              <Link
                to={item.id}
                className={className}
                aria-current={item.current ? "page" : undefined}
              >
                {content}
              </Link>
            )}
            footer={
              environment === undefined ? undefined : (
                <span data-testid="sidebar-environment">
                  {intl.formatMessage(navFooterMessage, { version: EXPLORER_VERSION, environment })}
                </span>
              )
            }
          />
        }
        topbar={
          <Topbar
            label={intl.formatMessage(shellMessages.topBarLabel)}
            start={<Breadcrumbs crumbs={crumbs} />}
            end={
              <>
                <CurrentInstallationBadge />
                <TopbarChip
                  icon={<GlobeIcon />}
                  label={intl.formatMessage(timeZoneMessage, { timeZone })}
                  title={timeZone}
                >
                  {offset}
                </TopbarChip>
                <TopbarMenuChip
                  label={intl.formatMessage(languageChipMessage, { locale: locale.toUpperCase() })}
                  icon={<LanguagesIcon />}
                  text={locale.toUpperCase()}
                >
                  {LOCALES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={menuClasses.item}
                      lang={value}
                      aria-current={value === locale ? "true" : undefined}
                      onClick={() => {
                        if (value === locale) return;
                        languageChosen.current = true;
                        setLocale(value);
                        saveSettings.mutate({ locale: value });
                      }}
                    >
                      {intl.formatMessage(LOCALE_LABELS[value])}
                    </button>
                  ))}
                </TopbarMenuChip>
                <TopbarToggleChip
                  label={intl.formatMessage(themeChipMessage, { theme: themeText })}
                  icon={theme === "light" ? <SunIcon /> : <MoonIcon />}
                  text={themeText}
                  onClick={() => {
                    const next = theme === "dark" ? "light" : "dark";
                    themeChosen.current = true;
                    setTheme(next);
                    saveSettings.mutate({ theme: next });
                  }}
                />
                <NotificationsMenu />
                <MenuButton
                  label={intl.formatMessage(shellMessages.apps)}
                  trigger={<LayoutGridIcon />}
                >
                  <Link to="/" aria-current="page" className={menuClasses.item}>
                    <ShieldCheckIcon aria-hidden="true" />
                    {intl.formatMessage(shellMessages.appsExplorer)}
                  </Link>
                  <button
                    type="button"
                    className={menuClasses.item}
                    aria-busy={openDocs.isPending || undefined}
                    onClick={() => {
                      if (!openDocs.isPending) openDocs.mutate();
                    }}
                  >
                    <ExternalLinkIcon aria-hidden="true" />
                    {intl.formatMessage(shellMessages.appsDocs)}
                  </button>
                  {/* spec integration-docs, "[FR] Documentation download for AI agents": the
                      same docs grant, then the file of the user's language, saved in place */}
                  <button
                    type="button"
                    className={menuClasses.item}
                    aria-busy={downloadLlms.isPending || undefined}
                    onClick={() => {
                      if (!downloadLlms.isPending) downloadLlms.mutate();
                    }}
                  >
                    <DownloadIcon aria-hidden="true" />
                    {intl.formatMessage(shellMessages.appsDocsLlms)}
                  </button>
                </MenuButton>
                {openDocs.isError ? (
                  <span role="alert" className={APPS_ERROR}>
                    {intl.formatMessage(shellMessages.appsDocsError)}
                  </span>
                ) : null}
                {downloadLlms.isError ? (
                  <span role="alert" className={APPS_ERROR}>
                    {intl.formatMessage(shellMessages.appsDocsLlmsError)}
                  </span>
                ) : null}
                <MenuButton
                  label={intl.formatMessage(shellMessages.user)}
                  trigger={
                    // The initials of the display name when the user set one (Settings); /me
                    // carries no email, so the user icon otherwise (design D11)
                    displayName === undefined ? (
                      <Avatar icon={<UserIcon />} />
                    ) : (
                      <Avatar initials={initialsOf(displayName)} />
                    )
                  }
                  triggerClassName={css({
                    p: "0",
                    bg: "transparent",
                    border: "none",
                    borderRadius: "pill",
                    cursor: "pointer",
                    _focusVisible: { outline: "none", boxShadow: "focus" },
                  })}
                >
                  {displayName === undefined ? null : (
                    <p className={MENU_NAME} data-testid="user-menu-name">
                      {displayName}
                    </p>
                  )}
                  <p className={MENU_TEXT}>{roleLabel}</p>
                  <hr className={menuClasses.separator} />
                  <Link
                    to="/settings"
                    className={menuClasses.item}
                    data-testid="user-menu-settings"
                  >
                    <UserCogIcon aria-hidden="true" />
                    {intl.formatMessage(userMenuMessages.settings)}
                  </Link>
                  <Button
                    visual="ghost"
                    size="sm"
                    icon={<LogOutIcon />}
                    loading={logout.isPending}
                    onClick={() => {
                      logout.mutate(undefined, {
                        onSettled: () => {
                          void navigate({ to: "/login", search: { reason: "signed_out" } }).then(
                            () => {
                              queryClient.clear();
                            },
                          );
                        },
                      });
                    }}
                  >
                    {intl.formatMessage(userMenuMessages.signOut)}
                  </Button>
                </MenuButton>
              </>
            }
          />
        }
        notice={
          saveSettings.isError ? (
            <p role="alert" className={NOTICE}>
              {intl.formatMessage(shellMessages.settingsSaveFailed)}
            </p>
          ) : null
        }
      >
        <div className={isWorkspaceRoute(pathname) ? WORKSPACE : CONTENT}>
          {me.isError ? <MeProblem error={me.error} onRetry={() => void me.refetch()} /> : children}
        </div>
      </AppFrame>
    </>
  );
}

/**
 * The session ended by expiry: same cleanup as a logout (CSRF token dropped, cache emptied after
 * leaving the signed-in screens) so no previous user's data stays in memory, then back to login.
 */
function SessionEnded() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    client.setCsrfToken(undefined);
    void navigate({ to: "/login", search: { reason: "session_ended" }, replace: true }).then(() => {
      queryClient.clear();
    });
  }, [client, navigate, queryClient]);
  return null;
}

function MeProblem({ error, onRetry }: { readonly error: Error; readonly onRetry: () => void }) {
  const intl = useIntl();
  // No live session (never signed in, idle 30 min, past 12 h, signed out elsewhere): back to login.
  if (error instanceof ApiRequestError && error.code === "unauthenticated") {
    return <SessionEnded />;
  }
  return (
    <ErrorState
      title={intl.formatMessage(shellMessages.meErrorTitle)}
      retryLabel={intl.formatMessage(appMessages.errorRetry)}
      onRetry={onRetry}
    >
      <p>{describeError(intl, error)}</p>
    </ErrorState>
  );
}
